import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Copy, CheckCircle, Mail, Download } from "lucide-react";
import { Button } from "../components/ui/button";
import { InternalNav } from "../components/InternalNav";

const emailTemplate = `OGGETTO: Proposta Virtual Tour 360° per [*NOME AGENZIA*] | schedabusiness.it

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

Gentile [*NOME RESPONSABILE*],

sono Riccardo Rossi, Fotografo Google Street View Certificato dal 2016.

Vi contatto perché [*NOME AGENZIA*] rappresenta un riferimento nel settore immobiliare [*della vostra zona / in Italia*] e credo che i Virtual Tour 360° possano portarvi vantaggi concreti.

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

I NUMERI PARLANO CHIARO

• 87% degli utenti preferisce esplorare virtualmente prima di visitare
• +42% richieste di contatto con tour virtuale
• -60% visite a vuoto

Un virtual tour può costare dai €500 ai €2.000 sul mercato.
Con noi, investite meno e ottenete di più.

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

I NOSTRI PACCHETTI

| Pacchetto | Prezzo   | Formazione        |
|-----------|----------|-------------------|
| START     | €99      | +€199             |
| PLUS      | €299     | +€149             |
| ROOFTOP   | €599     | IN REGALO         |

✓ Pagamento in 3 rate PayPal (TAEG 0%)
✓ Detraibile 100% come spesa pubblicitaria
✓ Pubblicazione su Google Maps inclusa
✓ Timewarp Hyperlapse IN REGALO in tutti i pacchetti

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

DEMO LIVE

Esplorate i nostri tour qui:
https://schedabusiness.it
https://schedabusiness.com

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

PROSSIMI PASSI

1. Demo [*gratuita / di 15 minuti*]
2. Scelta pacchetto
3. Riprese entro [*___*] giorni
4. Consegna entro [*___*] giorni lavorativi

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

CONTATTI

Riccardo Rossi
WhatsApp: +39 327 383 9782
[*LA TUA EMAIL*]
https://schedabusiness.it | https://schedabusiness.com
[*LINKEDIN*]

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

P.S. Posso passare in agenzia per una dimostrazione dal vivo.
L'offerta è valida [*fino al ___ / per i primi ___ clienti*].

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

NOTA FISCALE
Le spese per virtual tour sono integralmente deducibili come spese pubblicitarie (art. 108 TUIR).

[*FIRMA / LOGO*]`;

export default function EmailTemplate() {
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);

  const copyToClipboard = () => {
    navigator.clipboard.writeText(emailTemplate);
    setCopied(true);
    setTimeout(() => setCopied(false), 3000);
  };

  const downloadTxt = () => {
    const blob = new Blob([emailTemplate], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'template-email-virtual-tour.txt';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA] py-8 px-4 sm:px-6">
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-[#5F6368] hover:text-[#202124] mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Torna alla Home
          </button>

          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-3xl font-semibold text-[#202124] mb-2">
                Template Email <span className="text-[#4285F4]">Manifesto</span>
              </h1>
              <p className="text-[#5F6368]">
                Copia e personalizza i campi con [*asterischi*]
              </p>
            </div>
            <div className="flex gap-3">
              <Button
                onClick={copyToClipboard}
                className={`rounded-full ${copied ? 'bg-[#34A853]' : 'bg-[#4285F4]'} text-white`}
              >
                {copied ? <CheckCircle className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                {copied ? 'Copiato!' : 'Copia Tutto'}
              </Button>
              <Button
                onClick={downloadTxt}
                variant="outline"
                className="rounded-full border-[#DADCE0]"
              >
                <Download className="w-4 h-4 mr-2" />
                Scarica .txt
              </Button>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-[#DADCE0] shadow-sm overflow-hidden">
            <div className="bg-[#F8F9FA] px-6 py-3 border-b border-[#DADCE0] flex items-center gap-2">
              <Mail className="w-4 h-4 text-[#5F6368]" />
              <span className="text-sm font-medium text-[#5F6368]">Template Email B2B - Virtual Tour 360°</span>
            </div>
            <pre className="p-6 text-sm text-[#202124] whitespace-pre-wrap font-mono leading-relaxed overflow-x-auto">
              {emailTemplate}
            </pre>
          </div>

          <div className="mt-6 p-4 rounded-xl bg-[#E8F0FE] border border-[#4285F4]/20">
            <h3 className="font-semibold text-[#202124] mb-2">💡 Campi da personalizzare:</h3>
            <ul className="text-sm text-[#5F6368] space-y-1">
              <li>• <code className="bg-white px-1 rounded">[*NOME AGENZIA*]</code> - Nome dell'agenzia destinataria</li>
              <li>• <code className="bg-white px-1 rounded">[*NOME RESPONSABILE*]</code> - Nome del titolare o responsabile marketing</li>
              <li>• <code className="bg-white px-1 rounded">[*della vostra zona / in Italia*]</code> - Specifica geografica</li>
              <li>• <code className="bg-white px-1 rounded">[*___*]</code> - Tempistiche (es. 7 giorni, 14 giorni)</li>
              <li>• <code className="bg-white px-1 rounded">[*LA TUA EMAIL*]</code> - La tua email</li>
              <li>• <code className="bg-white px-1 rounded">[*LINKEDIN*]</code> - Il tuo profilo LinkedIn</li>
              <li>• <code className="bg-white px-1 rounded">[*FIRMA / LOGO*]</code> - Firma professionale</li>
            </ul>
          </div>

          <div className="mt-6 flex gap-4">
            <Button
              onClick={() => navigate("/agenzie")}
              variant="outline"
              className="rounded-full border-[#4285F4] text-[#4285F4]"
            >
              Vai alla Lista Agenzie
            </Button>
            <Button
              onClick={() => navigate("/roadmap")}
              variant="outline"
              className="rounded-full border-[#34A853] text-[#34A853]"
            >
              Roadmap Commerciale
            </Button>
          </div>

          <div className="mt-8 border-t border-[#DADCE0] pt-4">
            <InternalNav />
          </div>
        </motion.div>
      </div>
    </div>
  );
}
